import { useEffect, useRef } from 'react';
import { useDueSoon } from './useDueSoon';
import { useAudioStore } from '@/stores/audio.store';
import { toast } from '@/stores/toast.store';
import { useT } from '@/i18n';

export function useBookingAlerts(): void {
  const t = useT();
  const dueSoonQ = useDueSoon(30);
  const audioUnlocked = useAudioStore((s) => s.audioUnlocked);
  
  const alertedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!dueSoonQ.data) return;

    const now = new Date();

    // Check each booking to see if it falls within the alert window (now +/- 30 seconds tolerance)
    dueSoonQ.data.forEach((booking: any) => {
      const scheduledAt = new Date(booking.scheduledAt);
      const timeDiff = scheduledAt.getTime() - now.getTime(); // milliseconds
      const secondsDiff = timeDiff / 1000;

      // Alert if within 30 seconds of scheduled_at and not yet alerted
      if (Math.abs(secondsDiff) <= 30 && !alertedIdsRef.current.has(String(booking.id))) {
        // Mark as alerted
        alertedIdsRef.current.add(String(booking.id));

        // Get patient last name from embedded patient summary
        const patientName = booking.patient?.lastNameDecrypted || 'Patient';

        // Format time for display
        const timeStr = scheduledAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        // Fire VIP toast
        toast.vip(
          t('bookings.alert.title'),
          `${patientName} — ${t('bookings.alert.subtitle', { time: timeStr })}`
        );

        // Play chime sound (if audio is unlocked)
        if (audioUnlocked) {
          try {
            const audio = new Audio('/sounds/chime.wav');
            audio.volume = 0.5;
            audio.play().catch(() => {
              // Silently fail if autoplay is blocked (shouldn't happen if audioUnlocked, but safe guard)
            });
          } catch {
            // Silently fail
          }
        }
      }
    });
  }, [dueSoonQ.data, audioUnlocked, t]);
}
